import useAdminToast from '../useAdminToast';
import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import { Badge, HStack, Text } from '@chakra-ui/react';
import AdminState from '../AdminState';
import { tableIcons, tableOptions, tableComponents, presentTableAction } from './tablePresentation';

export default function DataTable({ submitRowChange, submitNewRow, submitRowDeletion,
   columns, dataFunction, title, customActions = [],
   errorMsg = 'Could not save these changes. Check the entered values and try again.',
}) {
   const toast = useAdminToast();
   const recordLabel = title.replace(/^Your space's /, '').replace(/^Archived /, '').toLowerCase();
   const [data, setData] = useState([]);
   const [loading, setLoading] = useState(true);
   const [hasLoaded, setHasLoaded] = useState(false);
   const [loadError, setLoadError] = useState(false);
   const [saveError, setSaveError] = useState(false);
   const [loadWarning, setLoadWarning] = useState('');
   const [actionError, setActionError] = useState('');
   const [actionPending, setActionPending] = useState(false);
   const actionPendingRef = useRef(false);
   const [retry, setRetry] = useState(0);

   useEffect(() => {
      let active = true;
      setLoading(true);
      setLoadError(false);
      setLoadWarning('');
      async function load() {
         try {
            const response = await dataFunction();
            if (response.status !== 200 || response.error) throw new Error('load_failed');
            if (active) {
               setData(response.items || []);
               setHasLoaded(true);
               setLoadWarning(response.alert || '');
            }
         } catch {
            if (active) setLoadError(true);
         } finally {
            if (active) setLoading(false);
         }
      }
      load();
      return () => { active = false; };
   }, [dataFunction, retry]);

   // Keep the editor open on failure so staff can correct values or retry.
   const save = async (submit, successMessage, ...args) => {
      setSaveError(false);
      let response;
      try {
         response = await submit(...args);
         if (response && (response.error || response.status === 0 || response.status >= 400)) {
            throw new Error('save_failed');
         }
         toast({ title: successMessage });
         setRetry(value => value + 1);
      } catch {
         setSaveError(typeof errorMsg === 'function' ? errorMsg(response) : errorMsg);
         throw new Error('save_failed');
      }
   };
   const editable = submitNewRow || submitRowChange || submitRowDeletion ? {
      ...(submitNewRow ? { onRowAdd: row => save(submitNewRow, 'Record created', row) } : {}),
      ...(submitRowChange ? { onRowUpdate: (row, oldRow) => save(submitRowChange, 'Changes saved', row, oldRow) } : {}),
      ...(submitRowDeletion ? { onRowDelete: row => save(submitRowDeletion, 'Record deleted', row) } : {}),
      onRowAddCancelled: () => setSaveError(false),
      onRowUpdateCancelled: () => setSaveError(false),
      onRowDeleteCancelled: () => setSaveError(false),
   } : undefined;

   const presentAction = action => {
      if (typeof action === 'function') return row => presentAction(action(row));
      return {
         ...presentTableAction(action),
         disabled: actionPending || action.disabled,
         onClick: async (...args) => {
            if (actionPendingRef.current) return;
            setActionError('');
            try {
               const result = action.onClick(...args);
               // Opening a confirmation dialog does not mutate table data.
               if (!result || typeof result.then !== 'function') return;
               actionPendingRef.current = true;
               setActionPending(true);
               const response = await result;
               if (response && (response.error || response.status === 0 || response.status >= 400)) {
                  throw new Error('action_failed');
               }
               toast({ title: 'Action completed' });
               setRetry(value => value + 1);
            } catch {
               setActionError(`Could not ${(action.tooltip || 'complete this action').toLowerCase()}. Try again.`);
            } finally {
               actionPendingRef.current = false;
               setActionPending(false);
            }
         },
      };
   };

   if (loading && !hasLoaded) return <AdminState status="loading" title={`Loading ${title.toLowerCase()}…`} />;
   if (loadError) return <AdminState status="error" title={`${title} could not be loaded`}
      description="Pinpoint could not load these records. Try again."
      onRetry={() => setRetry(value => value + 1)} />;

   return <>
      {loadWarning && <AdminState status="error" title="Some records could not be loaded" description={loadWarning}
         onRetry={() => setRetry(value => value + 1)} />}
      {saveError && <AdminState status="error" title="Changes could not be saved" description={saveError} />}
      {actionError && <AdminState status="error" title="Action could not be completed" description={actionError} />}
      <MaterialTable
         isLoading={loading || actionPending}
         icons={tableIcons}
         options={tableOptions}
         components={tableComponents}
         style={{ boxShadow: 'none', borderRadius: 12, overflow: 'hidden' }}
         title={<HStack spacing={2} className="admin-table-heading">
            <Text as="h2">{title}</Text><Badge colorScheme="gray">{data.length}</Badge>
         </HStack>}
         columns={columns}
         data={data}
         actions={customActions.map(presentAction)}
         editable={editable}
         localization={{ body: { editRow: { saveTooltip: 'Save', cancelTooltip: 'Cancel', deleteText: 'Delete this record?' }, emptyDataSourceMessage: <div className="admin-table-empty-state"><AdminState mb={0} textAlign="left"
            title={data.length ? `No matching ${recordLabel}` : title.startsWith('Archived') ? `No archived ${recordLabel}` : `No ${recordLabel} yet`}
            description={data.length ? 'Try a different search or clear the search field.' :
               title.startsWith('Archived') ? 'Archived records will appear here. Switch to Active to see current records.' :
               undefined} /></div> } }}
      />
   </>;
}
